import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import React, { useState } from 'react';
import useSWR from 'swr';
import { Loader2, CheckCircle2, Send } from 'lucide-react';

import { useT } from '@/contexts/I18nContext';
import { usePageTitle, usePageHeaderSlots } from '@/contexts/PageHeaderContext';
import { useCurrentZone } from '@/hooks/useCurrentZone';
import { useAppActions } from '@/app/appContext';
import { BranchRequiredState } from '@/components/common/BranchRequiredState';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getDailyReports, getDailyReportQueue, type DailyReportQueueKind } from '@/api/dailyReports';
import { ReportEditorV1 } from '@/components/report-validation/ReportEditorV1';
import type { EditorMode } from '@/components/report-validation/FormattedBodyEditor';
import { DateStepper, todayIso, shiftIso, fmtShort } from '@/pages/Usage/OverviewReportPage';

/**
 * Проверка отчётов — the offline-first report validation surface.
 *
 * V1 PREVIEW HARNESS (editors peer): a single-report formatted editor + consult
 * charts for the globally-selected branch + a day. The `shell-offline` peer owns
 * the real frame (per-branch sections, header branch/day pickers, offline store,
 * submit-all); when that lands it mounts `<ReportEditorV1 report=… />` per section
 * and this harness goes away. Staff-gated route + Служебные group are already wired.
 */
export default function ReportValidationPage() {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  usePageTitle(t('sidebar.reportValidation'));

  const { zoneId } = useCurrentZone();
  const { setZone } = useAppActions();
  // Reports cover the past day — default to yesterday, same as the regular
  // report view (OverviewReportPage), not today (Alexey 2026-09-04).
  const [day, setDay] = useState(() => shiftIso(todayIso(), -1));
  // item 10 (+ the ambiguous, likely-duplicate item 7 in Alexey's doc — same
  // underlying complaint): the mode was PLAIN internal state inside
  // ReportEditorV1, which unmounts during the isLoading gap every time the
  // date/branch changes (this page swaps spinner ⇄ "no report" ⇄ editor by
  // conditional rendering, not keepPreviousData) — so it silently reset to
  // "конструктор" on every switch. Lifted here, where the page itself stays
  // mounted across both switches.
  const [mode, setMode] = useState<EditorMode>('constructor');

  // Same day-stepper as the public /usage/report page (‹ date › arrows), so
  // validators get one consistent date-navigation feel across both pages.
  const { data: pendingQueue } = useSWR(['rv-queue', day, 'pending'], () =>
    getDailyReportQueue(day, 'pending'),
  );
  const { data: unsentQueue } = useSWR(['rv-queue', day, 'unsent'], () =>
    getDailyReportQueue(day, 'unsent'),
  );
  // Pre-existing bug fix (found while verifying round-2 items): the missing
  // second `?.` meant `pendingQueue?.results.length` threw on the brief
  // render before SWR resolves (pendingQueue itself undefined) instead of
  // safely short-circuiting — crashed the whole page with no error boundary
  // to catch it.
  const pendingCount = pendingQueue?.results?.length ?? 0;
  const unsentCount = unsentQueue?.results?.length ?? 0;

  // "Next unconfirmed"/"next unsent" jump the GLOBAL branch selector to the
  // next zone still in that queue for the current day (this INTERIM harness
  // is single-branch; shell-offline's full multi-section page replaces this
  // with in-page scrolling instead of branch-switching).
  const jumpToNext = (kind: DailyReportQueueKind) => {
    const results = (kind === 'pending' ? pendingQueue : unsentQueue)?.results ?? [];
    if (!results.length) return;
    const idx = results.findIndex((r) => String(r.zone_id) === String(zoneId));
    const next = results[(idx + 1) % results.length];
    if (next) setZone(String(next.zone_id));
  };

  usePageHeaderSlots(
    {
      center: (
        <div className="flex items-center gap-2">
          <DateStepper
            label={fmtShort(day, locale)}
            onPrev={() => setDay(shiftIso(day, -1))}
            onNext={() => setDay(shiftIso(day, 1))}
            nextDisabled={day >= todayIso()}
            pickerValue={day}
            onPickDate={setDay}
            pickerMax={todayIso()}
            locale={locale}
          />
          {pendingCount > 0 && (
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => jumpToNext('pending')}>
              <CheckCircle2 className="size-3.5" />
              {t('reportValidation.nextUnconfirmed')} ({pendingCount})
            </Button>
          )}
          {pendingCount === 0 && unsentCount > 0 && (
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => jumpToNext('unsent')}>
              <Send className="size-3.5" />
              {t('reportValidation.nextUnsent')} ({unsentCount})
            </Button>
          )}
        </div>
      ),
    },
    [day, lang, pendingCount, unsentCount, zoneId],
  );

  const { data, isLoading, error, mutate } = useSWR(
    zoneId ? ['report-validation', zoneId, day] : null,
    () => getDailyReports(zoneId!, day, day).then((r) => r.results[0] ?? null),
    // No focus-revalidation (item 12): a refresh here re-seeds ReportEditorV1's
    // whole editing state (bodyHtml/hiddenSections useEffect keys off
    // report.id/body_html) even when nothing actually changed — every alt-tab
    // back into the tab blinked the editor. Same fix as DailyReportView.
    { revalidateOnFocus: false },
  );

  if (!zoneId) {
    return (
      <div className="mx-auto w-full max-w-3xl p-4 sm:p-6">
        <BranchRequiredState />
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 p-4 sm:p-6">
      {isLoading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" />
          <span>{t('summaries.loading')}</span>
        </div>
      )}

      {!isLoading && error && !data && (
        <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />
      )}

      {!isLoading && !error && !data && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {t('summaries.noReport')}
          </CardContent>
        </Card>
      )}

      {!isLoading && data && (
        <ReportEditorV1
          report={data}
          zoneId={String(zoneId)}
          day={day}
          onActioned={() => mutate()}
          mode={mode}
          onModeChange={setMode}
        />
      )}
    </div>
  );
}
